import { useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  VButton,
  VField,
  VTextarea,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useAddCaseNote } from "@/domains/cases/api/mutations";
import type { CaseFile } from "@/domains/cases/api/types";

type CaseOverviewTabProps = {
  caseFile: CaseFile;
};

export function CaseOverviewTab({ caseFile }: CaseOverviewTabProps): React.JSX.Element {
  const currentUser = useCurrentUser();
  const addNote = useAddCaseNote();
  const [newNote, setNewNote] = useState("");

  const handleAddNote = (): void => {
    if (newNote.trim().length === 0) return;
    addNote.mutate(
      {
        caseId: caseFile.id,
        note: {
          authorId: currentUser.id,
          authorName: `${currentUser.firstName} ${currentUser.lastName}`,
          content: newNote.trim(),
          scope: "case",
        },
      },
      {
        onSuccess: () => setNewNote(""),
      },
    );
  };

  const caseNotes = caseFile.notes.filter((n) => n.scope === "case");

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card padding="lg" className="space-y-3 lg:col-span-2">
        <CardTitle>Dossier-info</CardTitle>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <Caption tone="muted">Adres</Caption>
            <Body weight="medium">{caseFile.address}</Body>
          </div>
          <div>
            <Caption tone="muted">Stadsdeel</Caption>
            <Body>{caseFile.district}</Body>
          </div>
          <div>
            <Caption tone="muted">Begin</Caption>
            <Body>{caseFile.startDate}</Body>
          </div>
          <div>
            <Caption tone="muted">Eind</Caption>
            <Body>{caseFile.endDate}</Body>
          </div>
          <div className="col-span-2">
            <Caption tone="muted">Reden inzet</Caption>
            <Body>{caseFile.reason}</Body>
          </div>
        </dl>
      </Card>

      <Card padding="lg" className="space-y-3">
        <CardTitle>Behandelaar</CardTitle>
        <Body weight="medium">{caseFile.managerName}</Body>
        <Caption tone="muted">Team functionele ondersteuning</Caption>
      </Card>

      <Card padding="lg" className="space-y-3 lg:col-span-3">
        <CardTitle>Opmerkingen op dossier-niveau</CardTitle>
        {caseNotes.length === 0 ? (
          <Caption tone="muted">Nog geen opmerkingen toegevoegd.</Caption>
        ) : (
          <ul className="space-y-3">
            {caseNotes.map((n) => (
              <li key={n.id} className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <div className="flex items-baseline justify-between gap-2">
                  <Body weight="semibold">{n.authorName}</Body>
                  <Caption tone="muted">{n.createdAt.slice(0, 16).replace("T", " ")}</Caption>
                </div>
                <Body>{n.content}</Body>
              </li>
            ))}
          </ul>
        )}
        <VField label="Nieuwe opmerking" htmlFor="note">
          <VTextarea
            id="note"
            rows={3}
            placeholder="Voeg een notitie toe…"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
        </VField>
        <div className="flex justify-end">
          <VButton
            onClick={handleAddNote}
            disabled={addNote.isPending || newNote.trim().length === 0}
          >
            <MessageSquarePlus className="size-4" aria-hidden />
            Toevoegen
          </VButton>
        </div>
      </Card>
    </div>
  );
}
